import type { OtimAPIClient } from "@api/api-client";
import type {
  GetConfigChainsResponse,
  GetConfigTokensResponse,
  GetDelegateAddressResponse,
  GetHealthResponse,
  GetInstructionStorageAddressRequest,
  GetInstructionStorageAddressResponse,
} from "@api/schemas/config";
import type {
  GetMaxPriorityFeePerGasEstimateRequest,
  GetMaxPriorityFeePerGasEstimateResponse,
} from "@api/schemas/instruction";

export interface GetTokensOptions {
  chainId: number;
}

export interface GetMaxPriorityFeeEstimateOptions {
  chainId: number;
}

export interface GetInstructionStorageAddressOptions {
  chainId: number;
}

export async function getTokens(
  client: OtimAPIClient,
  { chainId }: GetTokensOptions,
): Promise<GetConfigTokensResponse> {
  const response = await client.config.getTokens({ chainId });
  return response.data;
}

export async function getChains(
  client: OtimAPIClient,
): Promise<GetConfigChainsResponse> {
  const response = await client.config.getChains();
  return response.data;
}

export async function getMaxPriorityFeeEstimate(
  client: OtimAPIClient,
  { chainId }: GetMaxPriorityFeeEstimateOptions,
): Promise<GetMaxPriorityFeePerGasEstimateResponse> {
  const requestBody: GetMaxPriorityFeePerGasEstimateRequest = { chainId };
  const response =
    await client.instruction.getMaxPriorityFeePerGasEstimate(requestBody);
  return response.data;
}

export async function getHealth(
  client: OtimAPIClient,
): Promise<GetHealthResponse> {
  const response = await client.config.getHealth();
  return response.data;
}

export async function getInstructionStorageAddress(
  client: OtimAPIClient,
  { chainId }: GetInstructionStorageAddressOptions,
): Promise<GetInstructionStorageAddressResponse> {
  const requestBody: GetInstructionStorageAddressRequest = { chainId };
  const response =
    await client.config.getInstructionStorageAddress(requestBody);
  return response.data;
}

export async function getDelegateAddress(
  client: OtimAPIClient,
  { chainId }: { chainId: number },
): Promise<GetDelegateAddressResponse> {
  const response = await client.config.getDelegateAddress({ chainId });
  return response.data;
}

export interface ConfigClient {
  readonly chains: () => ReturnType<typeof getChains>;
  readonly health: () => ReturnType<typeof getHealth>;
  readonly getMaxPriorityFeeEstimate: (
    options: GetMaxPriorityFeeEstimateOptions,
  ) => ReturnType<typeof getMaxPriorityFeeEstimate>;
  readonly getDelegateAddress: (options: {
    chainId: number;
  }) => ReturnType<typeof getDelegateAddress>;
  readonly getInstructionStorageAddress: (
    options: GetInstructionStorageAddressOptions,
  ) => ReturnType<typeof getInstructionStorageAddress>;
  readonly getTokens: (
    options: GetTokensOptions,
  ) => ReturnType<typeof getTokens>;
}

export function createConfigClient(client: OtimAPIClient): ConfigClient {
  return {
    chains: () => getChains(client),
    health: () => getHealth(client),
    getMaxPriorityFeeEstimate: (options: GetMaxPriorityFeeEstimateOptions) =>
      getMaxPriorityFeeEstimate(client, options),
    getDelegateAddress: (options: { chainId: number }) =>
      getDelegateAddress(client, options),
    getInstructionStorageAddress: (
      options: GetInstructionStorageAddressOptions,
    ) => getInstructionStorageAddress(client, options),
    getTokens: (options: GetTokensOptions) => getTokens(client, options),
  };
}
